import { roleLevelOptions } from 'constants/roleLevel';
import {
  ContentRoleLevel,
  JobRoleFindManyPaginatedOrderField,
  JobRoleSource,
} from 'generated/graphql';
import { FC, MouseEvent, useEffect, useState } from 'react';
import {
  Button,
  SelectFormField,
  TSelectOption,
} from '@spotted-zebra-uk/ui-components';
import {
  JOB_ROLE_SOURCE_LABELS,
  JOB_ROLE_ORDER_BY_LABELS,
  TDuplicateJobRoleFilters,
} from './DuplicateJobRoleModal.helpers';
import styles from './DuplicateJobRoleModal.module.scss';

type TDuplicateJobRoleFiltersProps = {
  isOpen: boolean;
  appliedFilters: TDuplicateJobRoleFilters;
  onApply: (filters: TDuplicateJobRoleFilters) => void;
  onClose: () => void;
  lockedRoleLevel?: ContentRoleLevel;
};

const ANY = '';

const withAny = (
  options: { label: string; value: string }[],
  anyLabel: string,
): TSelectOption[] => [{ label: anyLabel, value: ANY }, ...options];

const SOURCE_OPTIONS = withAny(
  Object.values(JobRoleSource).map((value) => ({
    label: JOB_ROLE_SOURCE_LABELS[value],
    value,
  })),
  'Any source',
);

const ORDER_BY_OPTIONS = withAny(
  Object.values(JobRoleFindManyPaginatedOrderField)
    // We don't want to show the company name as an order by option as company id is set by the company of the role.
    .filter((value) => value !== JobRoleFindManyPaginatedOrderField.CompanyName)
    .map((value) => ({
      label: JOB_ROLE_ORDER_BY_LABELS[value],
      value: value as string,
    })),
  'Any order by',
);

const LEVEL_OPTIONS = withAny(roleLevelOptions, 'Any level');

const findOption = (options: TSelectOption[], value?: string) =>
  options.find((option) => option.value === (value ?? ANY));

// Native date inputs only open the calendar via their icon; open it on any
// click of the field (where the browser supports showPicker).
const openDatePicker = (event: MouseEvent<HTMLInputElement>) => {
  try {
    event.currentTarget.showPicker();
  } catch {
    // showPicker unsupported or blocked — the native calendar icon still works.
  }
};

const DuplicateJobRoleFilters: FC<TDuplicateJobRoleFiltersProps> = ({
  isOpen,
  appliedFilters,
  onApply,
  onClose,
  lockedRoleLevel,
}) => {
  const [draft, setDraft] = useState<TDuplicateJobRoleFilters>(appliedFilters);

  // Re-sync the draft to the applied filters each time the panel opens so a
  // discarded edit doesn't linger and applied selections are reflected.
  useEffect(() => {
    if (isOpen) setDraft(appliedFilters);
  }, [isOpen, appliedFilters]);

  const setField = <K extends keyof TDuplicateJobRoleFilters>(
    key: K,
    value: TDuplicateJobRoleFilters[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value || undefined }));

  if (!isOpen) return null;

  return (
    <div
      className={styles.filtersBody}
      data-testid="duplicate-job-role-modal__filters"
    >
      <div className={styles.filtersDateRange}>
        <label className={styles.filtersDateField}>
          <span className={styles.filtersDateLabel}>Created from</span>
          <input
            type="date"
            className={styles.filtersDateInput}
            value={draft.createdAtFrom ?? ''}
            max={draft.createdAtTo || undefined}
            onClick={openDatePicker}
            onChange={(event) => setField('createdAtFrom', event.target.value)}
          />
        </label>
        <label className={styles.filtersDateField}>
          <span className={styles.filtersDateLabel}>Created to</span>
          <input
            type="date"
            className={styles.filtersDateInput}
            value={draft.createdAtTo ?? ''}
            min={draft.createdAtFrom || undefined}
            onClick={openDatePicker}
            onChange={(event) => setField('createdAtTo', event.target.value)}
          />
        </label>
      </div>
      <div className={styles.filtersGroups}>
        <div className={styles.filtersRow}>
          <SelectFormField
            id="duplicate-job-role-order-by"
            label="Order by"
            placeholder="Order by"
            options={ORDER_BY_OPTIONS}
            value={findOption(ORDER_BY_OPTIONS, draft.orderBy)}
            onChange={(option: TSelectOption) =>
              setField(
                'orderBy',
                option?.value as JobRoleFindManyPaginatedOrderField,
              )
            }
          />
          <SelectFormField
            id="duplicate-job-role-filter-source"
            label="Job role source"
            placeholder="Role Skills Profile source"
            options={SOURCE_OPTIONS}
            value={findOption(SOURCE_OPTIONS, draft.source)}
            onChange={(option: TSelectOption) =>
              setField('source', option?.value as JobRoleSource)
            }
          />
        </div>

        <div className={styles.filtersRow}>
          <div className={styles.filtersRowHalf}>
            <SelectFormField
              id="duplicate-job-role-filter-level"
              label="Role level"
              placeholder="Role level"
              options={LEVEL_OPTIONS}
              isDisabled={Boolean(lockedRoleLevel)}
              value={
                lockedRoleLevel
                  ? findOption(LEVEL_OPTIONS, lockedRoleLevel)
                  : findOption(LEVEL_OPTIONS, draft.roleLevel)
              }
              onChange={(option: TSelectOption) =>
                setField('roleLevel', option?.value as ContentRoleLevel)
              }
              bottomText={
                lockedRoleLevel
                  ? `RSP(s) with this level are attached to this role.`
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      <div className={styles.filtersFooter}>
        <Button
          variant="tertiary"
          size="medium"
          onClick={() => {
            setDraft({});
            onApply({});
            onClose();
          }}
          data-testid="duplicate-job-role-modal__filters-reset"
        >
          Reset
        </Button>
        <div className={styles.filtersFooterActions}>
          <Button variant="secondary" size="medium" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
            data-testid="duplicate-job-role-modal__filters-apply"
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateJobRoleFilters;
